// What a badge copy is worth. Every repeat of a badge loses 4.5% of the value
// the copy before it had: the first copy is worth the badge's points, the second
// 95.5% of them, the third 95.5% of that, and so on.
//
//   copy k   = points * 0.955^(k-1)
//   N copies = points * (1 - 0.955^N) / 0.045
//
// The backend sends `value` on each copy and `totalValue` on each held badge
// (n450s_backend modules/badges/engine/value.ts, same rules). A backend older than
// that sends neither, so every reader goes through valueOf / totalValueOf, which
// fall back to computing it here.

export const REPEAT_DECAY = 0.045;
const KEEP = 1 - REPEAT_DECAY;

function finite(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/** A missing, fractional or out-of-range repeat index reads as the first copy. */
function safeIndex(repeatIndex: number | null | undefined): number {
  return finite(repeatIndex) && repeatIndex >= 1 ? Math.floor(repeatIndex) : 1;
}

/** Worth of one copy (repeatIndex is 1-based), full precision. */
export function copyValue(points: number, repeatIndex: number | null | undefined): number {
  return (finite(points) ? points : 0) * Math.pow(KEEP, safeIndex(repeatIndex) - 1);
}

/** Worth of the first `count` copies of a badge together, full precision. */
export function heldValue(points: number, count: number): number {
  const n = finite(count) ? Math.max(0, Math.floor(count)) : 0;
  if (n === 0 || !finite(points)) return 0;
  return (points * (1 - Math.pow(KEEP, n))) / REPEAT_DECAY;
}

/**
 * Two decimals, for display. The toPrecision step drops float noise first, so
 * 5 * 0.955 (4.7749999...) shows as 4.78, as the backend rounds it.
 */
export function roundValue(n: number): number {
  return finite(n) ? Math.round(Number((n * 100).toPrecision(12))) / 100 : 0;
}

/** This copy's worth: the server's `value`, else computed from points and repeatIndex. */
export function valueOf(copy: { value?: number | null; points?: number; repeatIndex?: number | null }): number {
  if (finite(copy.value)) return copy.value;
  return roundValue(copyValue(copy.points ?? 0, copy.repeatIndex));
}

/**
 * A held badge's worth across all its copies: the server's `totalValue`, else the
 * sum of its copies by repeatIndex when they are there, else the first `count`
 * copies.
 */
export function totalValueOf(badge: {
  totalValue?: number | null;
  points: number;
  count?: number;
  copies?: ReadonlyArray<{ repeatIndex?: number | null }>;
}): number {
  if (finite(badge.totalValue)) return badge.totalValue;
  const copies = badge.copies ?? [];
  if (copies.length > 0) {
    return roundValue(copies.reduce((sum, c) => sum + copyValue(badge.points, c.repeatIndex), 0));
  }
  return roundValue(heldValue(badge.points, Math.max(1, badge.count ?? 1)));
}
